import React from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import throttle from "lodash/throttle"
import loadMonaco from "shared/lib/load_monaco"
import es5LibAsString from "!raw-loader!./2_9_2_lib.es5.d.ts"

const es5Filename = "lib.es5.d.ts"
const contextFilename = "context.d.ts"

/**
 * This is a React wrapper for the Monaco editor. It takes up the full width of
 * whatever container it's placed in. Note that this doesn't directly depend on
 * the Monaco module because Monaco wants to load itself dynamically at
 * runtime, which is probably a good thing for our bundle size anyway.
 *
 * This is an uncontrolled component since I'm not sure how being controlled
 * would interact with Monaco's autocomplete, etc.
 *
 * Mostly borrowed from:
 * - https://github.com/superRaytin/react-monaco-editor
 * - https://github.com/Microsoft/monaco-editor/blob/master/website/playground
 */
export default class MonacoEditor extends React.Component {
  constructor(props) {
    super(props)
    this.initMonaco = this.initMonaco.bind(this)

    this.updateEditorLayout = throttle(this.updateEditorLayout.bind(this), 250)
  }

  // Never re-render after the initial render; in order to minimize weird
  // interactions with the editor, we want to just render once and handle any
  // further updates manually.
  shouldComponentUpdate() {
    return false
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    this.updateTypescriptContext(newProps.typescriptContext)
  }

  componentDidMount() {
    window.addEventListener("resize", this.updateEditorLayout)

    loadMonaco().then(this.initMonaco)
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updateEditorLayout)

    if (this._extraLibHandle) {
      this._extraLibHandle.dispose()
    }

    if (this._editor) {
      this._editor.dispose()
    }
  }

  initMonaco() {
    this._editor = window.monaco.editor.create(this.editorRef, {
      value: this.props.defaultValue,
      language: this.props.language,
      lineNumbersMinChars: 3,
      overviewRulerLanes: 0,
      scrollBeyondLastLine: false,
      wrappingColumn: 0,
      wrappingIndent: "indent",
      tabCompletion: true,
      folding: false,
      roundedSelection: false,
      contextmenu: false,
      renderLineHighlight: false,
      iconsInSuggestions: false,
      fontFamily:
        '"Menlo", "SFMono-Regular", "Consolas", "Liberation Mono", monospace', // Matches the `$code` var in 'foundation/_typography.scss'
      codeLens: false,

      fontSize: 14,
      suggestLineHeight: 18 + 2 * 8, // 8px of extra padding (cf `.suggest-widget` styles in CSS)
    })

    this._editor.getModel().updateOptions({
      tabSize: 2,
      insertSpaces: true,
      trimAutoWhitespace: true,
    })

    // Add an ES5 definitions file instead of using the default lib, because
    // the default one includes a bunch of Web APIs that aren't relevant.
    if (this.props.language === "typescript") {
      const tsDefaults = window.monaco.languages.typescript.typescriptDefaults

      if (!tsDefaults.getExtraLibs()[es5Filename]) {
        tsDefaults.addExtraLib(es5LibAsString, es5Filename)
      }
    }

    if (this.props.typescriptContext) {
      this.updateTypescriptContext(this.props.typescriptContext)
    }

    this._editor.onDidChangeModelContent(event => {
      const value = this._editor.getValue()
      this.props.onChange(value, event)
    })
  }

  updateEditorLayout() {
    if (this._editor) {
      this._editor.layout()

      // FF/monaco editor has a delay to identify the correct width after resize. Re-run after 300ms for FF users.
      if (this._editor.getView().domNode.classList.contains("ff")) {
        setTimeout(() => this._editor.layout(), 300)
      }
    }
  }

  updateTypescriptContext(newContext) {
    if (!this._editor || this._typescriptContext === newContext) {
      return
    }

    const tsDefaults = window.monaco.languages.typescript.typescriptDefaults

    if (this._extraLibHandle) {
      this._extraLibHandle.dispose()
    }
    this._typescriptContext = newContext
    this._extraLibHandle = tsDefaults.addExtraLib(newContext, contextFilename)

    // Setting the options here has the extra purpose of forcing the editor to
    // refresh its diagnostics to reflect the new TypeScript context.
    tsDefaults.setCompilerOptions({
      ...tsDefaults.getCompilerOptions(),
      noImplicitAny: true,
      noLib: true,
      noResolve: true,
    })
  }

  render() {
    const { positionAbsolute } = this.props
    const height = 480

    return (
      <div className="monaco-editor-wrapper-outer">
        <div
          className={classNames({
            "monaco-editor-wrapper-inner": positionAbsolute,
          })}
          ref={r => (this.editorRef = r)}
          style={{ width: "100%", height }}
        />
        {positionAbsolute && (
          <div
            className="monaco-editor-wrapper-placeholder"
            style={{ height }}
          />
        )}
      </div>
    )
  }
}

MonacoEditor.propTypes = {
  positionAbsolute: PropTypes.bool,
  defaultValue: PropTypes.string,
  language: PropTypes.oneOf(["typescript"]), // others are available, see docs
  onChange: PropTypes.func,
  typescriptContext: PropTypes.string,
}

MonacoEditor.defaultProps = {
  positionAbsolute: true,
  defaultValue: "",
  language: "typescript",
  onChange: () => {},
}
